import { createStore } from 'vuex';
import VuexPersistence from 'vuex-persist';
import { API, setToken } from '@/Service/AuthService';

export const DEFAULT_DEPLOYMENT = {
  title: "Tasman_Bay 119",
  label: "Tasman Bay (TASCAM Buoy)",
  disabled: false
};

const state = {
  site: DEFAULT_DEPLOYMENT.site,
  elements: ["waves", "wind", "temp", 'pressure'],
  highContrast: true,
  auth: {
    token: null,
    expiresAt: null,
    user: null
  },
  deployments: null,
  currentDeployment: DEFAULT_DEPLOYMENT
};

const mutations = {
  setSite(state, newSite) {
    state.site = newSite;
  },
  setElements(state, newElements) {
    state.elements = newElements;
  },
  setContrast(state, newContrast) {
    state.highContrast = newContrast;
  },
  SET_AUTH(state, { token, expiresAt, user }) {
    state.auth.token = token;
    state.auth.expiresAt = expiresAt;
    state.auth.user = user;
  },
  CLEAR_AUTH(state) {
    state.auth.token = null;
    state.auth.expiresAt = null;
    state.auth.user = null;
  },
  SET_DEPLOYMENTS(state, deployments) {
    state.deployments = deployments;
  },
  SET_CURRENT_DEPLOYMENT(state, deployment) {
    state.currentDeployment = deployment;
  }
};

const actions = {
  async setSite({ commit, state, dispatch }, site) {
    console.log('Setting site:', site);
    
    let deployments = state.deployments;
    if (!deployments) {
      try {
        deployments = await dispatch('fetchDeployments');
      } catch (error) {
        console.error('Error fetching deployments:', error);
        deployments = [DEFAULT_DEPLOYMENT];
      }
    }

    const deployment = deployments.find(d => d.site === site);
    console.log('Looking for deployment:', {
      searchSite: site,
      found: !!deployment,
      availableDeployments: deployments.map(d => d.site)
    });

    if (deployment) {
      console.log('Found deployment:', deployment);
      commit('SET_CURRENT_DEPLOYMENT', deployment);
      commit('setSite', deployment.site);
      
      const elements = ["waves", "wind", "temp", 'pressure'];
      commit('setElements', elements);
      return true;
    } else {
      console.warn('Deployment not found:', site);
      if (state.currentDeployment) {
        console.log('Keeping current deployment:', state.currentDeployment.site);
        return false;
      }
      
      console.log('Setting default deployment');
      commit('SET_CURRENT_DEPLOYMENT', DEFAULT_DEPLOYMENT);
      commit('setSite', DEFAULT_DEPLOYMENT.site);
      return false;
    }
  },
  
  setContrast: ({ commit }, contrast) => {
    commit('setContrast', contrast);
  },

  setAuth: ({ commit, dispatch }, { token, user }) => {
    let expiresAt = null;
    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        expiresAt = tokenPayload.exp * 1000;
        setToken(token);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }

    console.log('Storing auth data in Vuex:', { token: token ? 'present' : 'none', user });
    commit('SET_AUTH', { token, expiresAt, user });

    if (token) {
      dispatch('fetchDeployments');
    }
  },

  clearAuth: ({ commit }) => {
    setToken(null);
    commit('CLEAR_AUTH');
  },

  async fetchDeployments({ commit, state, dispatch }) {
    try {
      console.log('Fetching deployments...');
      const response = await API.get('/Deployments');
      
      const sources = response.data.sources;
      const cawOosSource = sources.find(source => 'CAW_OOS' in source);
      let deployments = cawOosSource ? cawOosSource.CAW_OOS : [];
      
      deployments = deployments.filter(d => !d.disabled);
      
      const hasDefaultDeployment = deployments.some(d => d.site === DEFAULT_DEPLOYMENT.site);
      if (!hasDefaultDeployment) {
        // deployments = [DEFAULT_DEPLOYMENT, ...deployments];
        console.log('Added default deployment to list');
      }

      console.log('Deployments processed:', {
        total: deployments.length,
        defaultIncluded: hasDefaultDeployment,
        deployments: deployments.map(d => ({
          site: d.site,
          label: d.label
        }))
      });
      
      commit('SET_DEPLOYMENTS', deployments);

      const currentSiteValid = deployments.some(d => d.site === state.site);
      if (!currentSiteValid) {
        console.log('Current site not found in deployments:', state.site);
        console.log('Available sites:', deployments.map(d => d.site));
        await dispatch('setSite', DEFAULT_DEPLOYMENT.site);
      }

      return deployments;
    } catch (error) {
      console.error('Error fetching deployments:', error);
      const fallbackDeployments = [DEFAULT_DEPLOYMENT];
      commit('SET_DEPLOYMENTS', fallbackDeployments);
      
      if (!state.currentDeployment || state.currentDeployment.site !== DEFAULT_DEPLOYMENT.site) {
        await dispatch('setSite', DEFAULT_DEPLOYMENT.site);
      }
      
      return fallbackDeployments;
    }
  }
};

const getters = {
  getAuthToken: state => state.auth.token,
  isAuthenticated: state => {
    if (!state.auth.token || !state.auth.expiresAt) return false;
    return state.auth.expiresAt > Date.now();
  },
  getUser: state => state.auth.user,
  getTokenExpiration: state => state.auth.expiresAt,
  getDeployments: state => state.deployments || [DEFAULT_DEPLOYMENT],
  getCurrentDeployment: state => state.currentDeployment || DEFAULT_DEPLOYMENT,
  getDeploymentBySite: state => site => {
    if (site === DEFAULT_DEPLOYMENT.site) return DEFAULT_DEPLOYMENT;
    return (state.deployments || []).find(d => d.site === site);
  },
  getCurrentSite: state => state.site || DEFAULT_DEPLOYMENT.site,
  isValidDeployment: state => site => {
    return (state.deployments || []).some(d => d.site === site) || 
          site === DEFAULT_DEPLOYMENT.site;
  }
};

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state) => ({
    site: state.site,
    elements: state.elements,
    highContrast: state.highContrast,
    auth: state.auth,
    currentDeployment: state.currentDeployment,
    deployments: state.deployments
  })
});

export default createStore({
  state,
  getters,
  actions,
  mutations,
  plugins: [vuexLocal.plugin],
});